import React, { FC, PropsWithChildren, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastsProvider, ToastsProviderProps } from 'tombac';
import { AuthApiClientProvider } from './authApi/AuthApiClientContext';
import { useMeQuery, useRefreshTokenMutation } from './authApi/authApiHooks';
import { LegolandApiClientProvider } from './legolandApi';
import { LegolandProvider, LegolandProviderProps } from './LegolandContext';
import { NewReleaseNotification } from './releaseNotes/NewReleaseNotification';

export interface LegolandAppProps extends LegolandProviderProps {
  /* Do the refresh-token requests periodically in the background (default = true). */
  autoRefreshToken?: boolean;
  /* Enable notifications about new releases (default = true). */
  newReleaseNotifications?: boolean;
  /* Time in milliseconds between the subsequent refresh-token calls (default = 1 minute). Requires autRefreshToken to be true. */
  refreshTokenInterval?: number;
  /* Props passed to Tombac's ToastsProvider. */
  toastsProviderProps?: ToastsProviderProps;
}

const AuthManager: FC<
  PropsWithChildren<Omit<LegolandAppProps, 'authApiUrl'>>
> = ({ autoRefreshToken = true, children, refreshTokenInterval = 60000 }) => {
  const meQuery = useMeQuery();
  const { mutate: refreshToken } = useRefreshTokenMutation();

  useEffect(
    function onMeChangeEffect() {
      if (!autoRefreshToken || !meQuery.isSuccess || meQuery.data.me === null) {
        return;
      }
      refreshToken();
      const interval = setInterval(refreshToken, refreshTokenInterval);
      return () => clearInterval(interval);
    },
    [
      autoRefreshToken,
      meQuery.isSuccess,
      meQuery.data?.me,
      meQuery.dataUpdatedAt,
    ],
  );

  return <>{children}</>;
};

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 2000 } },
});

const toastsProviderDefaults: ToastsProviderProps = {
  topOffset: 'navbar',
  zIndex: 10000,
};

export const LegolandApp: FC<PropsWithChildren<LegolandAppProps>> = ({
  children,
  newReleaseNotifications = true,
  toastsProviderProps,
  ...rest
}) => (
  <ToastsProvider {...{ ...toastsProviderDefaults, ...toastsProviderProps }}>
    <QueryClientProvider client={queryClient}>
      <LegolandProvider {...rest}>
        <AuthApiClientProvider>
          <LegolandApiClientProvider>
            <AuthManager {...rest}>
              {newReleaseNotifications && rest.releaseNotesUrl && (
                <NewReleaseNotification />
              )}
              {children}
            </AuthManager>
          </LegolandApiClientProvider>
        </AuthApiClientProvider>
      </LegolandProvider>
    </QueryClientProvider>
  </ToastsProvider>
);
